import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { useAcceptedMoves } from '../../lib/accepted-move';
import { useCardMove } from '../../lib/card-move';
import { undoShown, undoText } from '../../lib/move-note';
import { MoveIcon } from './MoveIcon';

// Where moved work went (#6772, prototype moveNoteHTML `:1779-1785`): a check-icon note in Now
// for a job move, in Mission for an epic move, on the target card when it is rendered, else on
// the source (`lib/move-note.ts`). Its Undo follows the server: "Undo once it lands" while any
// item is in flight, "Undo in N s" while the batch rests, then live for 7 s; `aria-disabled`
// (focusable, saying why in its `title`) until then, and while the page's own pre-send check on
// the batch's target holds (`MovePage.blockedWhy`). A batch with a failed item shows no Undo.
// Only a card with a move handle (an admin on an own-org machine) shows the note.

export function MoveNote(props: { readonly zone: 'now' | 'mission' }): JSX.Element {
  const a = useAcceptedMoves();
  const m = useCardMove();
  const note = () => {
    const n = a?.note() ?? null;
    if (n === null || m === null || !m.on()) return null;
    return n.boxId === m.box().id && n.zone === props.zone ? n : null;
  };
  return (
    <Show when={note()}>
      {(n) => {
        const blocked = (): string | null => m?.page.blockedWhy(n().undoFrom) ?? null;
        const live = (): boolean =>
          n().undo.kind === 'live' && blocked() === null && a?.undoing() !== true;
        return (
          <p class="move-note" role="status">
            <span class="move-note__text">
              <MoveIcon glyph="check" />
              <span>{n().text}</span>
            </span>
            <Show when={undoShown(n().undo)}>
              <button
                type="button"
                class="move-note__undo"
                aria-disabled={live() ? undefined : 'true'}
                title={
                  blocked() ??
                  t('fleet.move.undo_title', {
                    name: m?.page.boxOf(n().undoTo)?.display_name ?? n().undoTo.slice(0, 8),
                  })
                }
                onClick={() => {
                  if (live()) a?.undo();
                  else if ((m?.page.locked() ?? 0) > 0) {
                    m?.page.announce(t('fleet.machines.ctl_model_locked'));
                  }
                }}
              >
                {undoText(n().undo, m?.page.clock() ?? 0, t)}
              </button>
            </Show>
          </p>
        );
      }}
    </Show>
  );
}
